import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, syncApi } from '@/lib/api-client';

// ── Dashboard API (config CRUD — super admin) ──

export function useOctopusIntegration(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['octopus-integration', tenantId],
    queryFn: () => api.get<{ data: any }>(`/admin/octopus-integration/${tenantId}`),
    enabled: !!tenantId,
  });
}

export function useSaveOctopusIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      tenantId: string;
      apiUrl: string;
      apiToken: string;
      isActive?: boolean;
      syncMode?: 'interval' | 'daily' | 'scheduled';
      syncIntervalSec?: number;
      syncDailyTimePst?: string | null;
      syncScheduleTimes?: string[] | null;
    }) => api.post<{ data: any }>('/admin/octopus-integration', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-integration'] });
    },
  });
}

export function useTestOctopusConnection() {
  return useMutation({
    mutationFn: (data: { apiUrl: string; apiToken: string }) =>
      api.post<{ data: { success: boolean; message: string } }>('/admin/octopus-integration/test', data),
  });
}

// ── Sync API (sync operations — pass tenantId via header) ──

export function useOctopusSyncStatus(tenantId: string | undefined, limit = 10) {
  return useQuery({
    queryKey: ['octopus-sync-status', tenantId, limit],
    queryFn: () => syncApi.get<{ data: any[] }>('/octopus/sync/status', { limit }, tenantId),
    refetchInterval: 5000,
    enabled: !!tenantId,
  });
}

export function useTriggerOctopusSync(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => syncApi.post<{ data: any }>('/octopus/sync/trigger', undefined, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-sync-status'] });
      queryClient.invalidateQueries({ queryKey: ['octopus-sync-stats'] });
    },
  });
}

export function useTriggerFullOctopusSync(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => syncApi.post<{ data: any }>('/octopus/sync/trigger-full', undefined, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-sync-status'] });
      queryClient.invalidateQueries({ queryKey: ['octopus-sync-stats'] });
    },
  });
}

export function useCancelOctopusSync(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => syncApi.post<{ data: any }>('/octopus/sync/cancel', undefined, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-sync-status'] });
      queryClient.invalidateQueries({ queryKey: ['octopus-sync-stats'] });
    },
  });
}

export function useForceCancelOctopusSync(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => syncApi.post<{ data: any }>('/octopus/sync/force-cancel', undefined, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-sync-status'] });
      queryClient.invalidateQueries({ queryKey: ['octopus-sync-stats'] });
    },
  });
}

export function useDiscoverOctopusLocations(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      syncApi.get<{ data: { locationId: number; productCount: number }[] }>(
        '/octopus/locations/discover',
        undefined,
        tenantId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-location-mappings', tenantId] });
    },
  });
}

export function useOctopusLocationMappings(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['octopus-location-mappings', tenantId],
    queryFn: () =>
      syncApi.get<{
        data: {
          id: string;
          octopusLocationId: number;
          octopusLocationName: string | null;
          dashboardLocationId: string | null;
          dashboardLocationName: string | null;
          isActive: boolean;
        }[];
      }>('/octopus/locations/mappings', undefined, tenantId),
    enabled: !!tenantId,
  });
}

export function useSaveOctopusLocationMappings(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      mappings: { octopusLocationId: number; dashboardLocationId: string | null; octopusLocationName?: string }[],
    ) => syncApi.post<{ data: any[] }>('/octopus/locations/mappings', { mappings }, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-location-mappings'] });
    },
  });
}

export function useDeleteOctopusLocationMapping(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => syncApi.delete<{ data: any }>(`/octopus/locations/mappings/${id}`, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-location-mappings'] });
    },
  });
}

// ── Vendor selection (super-admin filter on which products are pulled) ──

export interface OctopusVendorSelection {
  id: string;
  octopusVendorId: number;
  octopusVendorName: string | null;
  isActive: boolean;
}

export function useDiscoverOctopusVendors(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => syncApi.get<{ data: { discovered: number } }>('/octopus/vendors/discover', undefined, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-vendor-selections', tenantId] });
    },
  });
}

export function useOctopusVendorSelections(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['octopus-vendor-selections', tenantId],
    queryFn: () => syncApi.get<{ data: OctopusVendorSelection[] }>('/octopus/vendors/selections', undefined, tenantId),
    enabled: !!tenantId,
  });
}

export function useSaveOctopusVendorSelections(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (activeVendorIds: number[]) =>
      syncApi.post<{ data: { activeCount: number } }>('/octopus/vendors/selections', { activeVendorIds }, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-vendor-selections', tenantId] });
    },
  });
}

// ── Unit mappings (admin-configurable Octopus unit ID → name) ──

export interface OctopusUnitMapping {
  id: string;
  octopusUnitId: number;
  unitName: string;
  conversionQty: number | null;
}

export function useOctopusUnitMappings(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['octopus-unit-mappings', tenantId],
    queryFn: () => syncApi.get<{ data: OctopusUnitMapping[] }>('/octopus/units/mappings', undefined, tenantId),
    enabled: !!tenantId,
  });
}

export function useSaveOctopusUnitMappings(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mappings: { octopusUnitId: number; unitName: string; conversionQty?: number | null }[]) =>
      syncApi.post<{ data: OctopusUnitMapping[] }>('/octopus/units/mappings', { mappings }, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-unit-mappings', tenantId] });
    },
  });
}

export function useDeleteOctopusUnitMapping(tenantId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => syncApi.delete<{ data: any }>(`/octopus/units/mappings/${id}`, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['octopus-unit-mappings', tenantId] });
    },
  });
}

export function useOctopusSyncConfig(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['octopus-sync-config', tenantId],
    queryFn: () => syncApi.get<{ data: any }>('/octopus/config', undefined, tenantId),
    enabled: !!tenantId,
  });
}

export function useOctopusSyncStats(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['octopus-sync-stats', tenantId],
    queryFn: () => syncApi.get<{ data: SyncStats }>('/octopus/sync/stats', undefined, tenantId),
    enabled: !!tenantId,
    refetchInterval: 5000,
  });
}

export interface SyncStats {
  integration: {
    isActive: boolean;
    syncMode?: string;
    syncIntervalSec: number;
    syncDailyTimePst?: string | null;
    syncScheduleTimes?: string | null;
    apiUrl: string;
  } | null;
  productsMapped: number;
  locations: { total: number; mapped: number };
  lastSync: {
    status: string;
    startedAt: string;
    completedAt: string | null;
    productsCreated: number;
    productsUpdated: number;
    errorMessage: string | null;
  } | null;
  last24h: PeriodStats;
  last7d: PeriodStats;
  recentRuns: SyncRun[];
}

export interface PeriodStats {
  totalRuns: number;
  successRuns: number;
  failedRuns: number;
  successRate: number;
  totalCreated: number;
  totalUpdated: number;
  totalSkipped: number;
}

export interface SyncRun {
  id: string;
  syncType: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  productsCreated: number;
  productsUpdated: number;
  productsSkipped: number;
  skippedMissingUnits: number;
  skippedMissingConversion: number;
  skippedUnknownUnits: number;
  totalProducts: number;
  processedProducts: number;
  currentPhase: string;
  errorMessage: string | null;
}
